import React, {
  forwardRef,
  PropsWithChildren,
  ReactElement,
  RefAttributes,
  RefObject,
  useEffect,
  useState,
} from 'react'
import {
  StyleSheet,
  View,
  TextInput as RawTextInput,
  TextInputProps,
  NativeSyntheticEvent,
  TextInputFocusEventData,
  Platform,
  TextStyle,
  StyleProp,
  ViewStyle,
  ViewProps,
} from 'react-native'
import { Hoverable } from 'react-native-web-hooks'

import { Color, Radius, Spacing } from '../../style'
import Icon, { Name } from '../icon/icon'
import { TextStyles } from '../typography/text'

import { Text } from '../typography'

import SecureIcon from './secureicon'

const styles = StyleSheet.create({
  input: {
    flex: 1,
    height: 38,
    ...Platform.select({
      ios: {
        paddingTop: 0,
        paddingBottom: 7,
      },
    }),
    verticalAlign: 'middle',
    color: Color.premiumBlack,
    ...TextStyles[6],
  },
  inlineDisabled: {
    backgroundColor: Color.sleepingGrey,
  },
  inline: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: Radius.S,
    borderColor: Color.calmGrey,
    paddingHorizontal: Spacing.S,
    backgroundColor: Color.simplyWhite,
  },
  inlineRound: {
    borderRadius: Radius.L,
  },
  inlineFilled: {
    borderColor: Color.premiumBlack,
  },
  inlineError: {
    borderColor: Color.soothingPink,
  },
  multiline: {
    paddingVertical: 12,
    height: 'auto',
  },
  outline: {
    borderColor: Color.calmGrey,
    borderWidth: 1,
    borderRadius: Radius.S + 1,
    margin: -1,
  },
  outlineRound: {
    borderRadius: Radius.L + 1,
  },
  outlineFilled: {
    borderColor: Color.premiumBlack,
  },
  outlineError: {
    borderColor: Color.soothingPink,
  },
  outlineActive: {
    borderWidth: 2,
    borderColor: Color.greyBlue,
    margin: -2,
  },
  left: {
    paddingLeft: Spacing.XS,
  },
  right: {
    paddingRight: Spacing.XS,
  },
  skeletonContainer: {
    minHeight: 40,
  },
  skeletonText: {
    flex: 1,
  },
  skeletonMultiline: {
    marginTop: 3,
    marginBottom: 3,
  },
})

export interface Props extends TextInputProps {
  left?: Name | ReactElement
  right?: Name | ReactElement
  error?: boolean
  disabled?: boolean
  round?: boolean
  inlineStyle?: StyleProp<ViewStyle>
  inputStyle?: StyleProp<TextStyle>
  inputRef?: RefObject<RawTextInput>
}

const isEmpty = (value?: string) => {
  return value === undefined || (typeof value === 'string' && value.length <= 0)
}

const TextInput = forwardRef<RawTextInput, Props>((props, ref) => {
  const {
    style,
    inlineStyle,
    inputStyle,
    left,
    right,
    error,
    disabled,
    round,
    onFocus,
    onBlur,
    multiline,
    numberOfLines,
    onChangeText,
    secureTextEntry,
    ...otherProps
  } = props
  const [active, setActive] = useState(false)
  const [secure, setSecure] = useState(secureTextEntry)
  const [isFilled, setIsFilled] = useState(!isEmpty(props.value))

  const updateActive = (value: boolean) => (event: NativeSyntheticEvent<TextInputFocusEventData>) => {
    setActive(value)
    value && onFocus && onFocus(event)
    !value && onBlur && onBlur(event)
  }
  useEffect(() => {
    if (disabled) setActive(false)
  }, [disabled])

  useEffect(() => {
    setIsFilled(!isEmpty(props.value))
  }, [props.value])

  const onUpdate = (text: string) => {
    setIsFilled(!isEmpty(text))
    onChangeText && onChangeText(text)
  }

  const renderIcon = (icon: Name | ReactElement) => {
    if (typeof icon === 'string') {
      return <Icon name={icon} size={16} pointerEvents="none" />
    }
    return icon
  }

  const renderInput = (hovered: boolean) => {
    return (
      <View
        style={[
          (hovered || active) && !disabled && styles.outline,
          round && styles.outlineRound,
          isFilled && !disabled && styles.outlineFilled,
          active && !disabled && styles.outlineActive,
          !!error && styles.outlineError,
          style,
        ]}
      >
        <View
          style={[
            styles.inline,
            round && styles.inlineRound,
            disabled && styles.inlineDisabled,
            isFilled && !disabled && styles.inlineFilled,
            !!error && styles.inlineError,
            inlineStyle,
          ]}
        >
          {!!left && renderIcon(left)}
          <RawTextInput
            ref={ref}
            style={[
              styles.input,
              Platform.select({
                // @ts-expect-error outline style none is not defined
                web: {
                  outlineStyle: 'none',
                } as TextStyle,
              }),
              multiline && styles.multiline,
              left && styles.left,
              right && styles.right,
              inputStyle,
            ]}
            placeholderTextColor={Color.calmGrey}
            onFocus={updateActive(true)}
            onBlur={updateActive(false)}
            multiline={multiline}
            numberOfLines={multiline ? numberOfLines || 4 : undefined}
            onChangeText={onUpdate}
            editable={!disabled}
            secureTextEntry={secure}
            testID="TextInput.Input"
            {...otherProps}
          />
          {secureTextEntry && <SecureIcon value={secure} onChange={setSecure} testID="TextInput.Secure" />}
          {!!right && renderIcon(right)}
        </View>
      </View>
    )
  }

  return <Hoverable>{renderInput}</Hoverable>
}) as ((props: PropsWithChildren<Props> & RefAttributes<View>) => ReactElement | null) & {
  Skeleton: (props: SkeletonProps) => ReactElement
}

interface SkeletonProps extends ViewProps {
  multiline?: boolean
  contentStyle?: StyleProp<ViewStyle>
}

TextInput.Skeleton = (props: SkeletonProps) => {
  const { multiline, contentStyle, style, ...otherProps } = props

  const containerStyle = [styles.inline, multiline && styles.multiline, styles.skeletonContainer, style]
  const textStyle = [contentStyle, multiline && styles.skeletonMultiline]
  return (
    <View style={containerStyle} {...otherProps}>
      <Text.Skeleton style={styles.skeletonText} contentStyle={textStyle} numberOfLines={multiline ? 3 : 1} />
    </View>
  )
}

export default TextInput
